import { Injectable } from '@angular/core';
import { ConvexService } from '@masar/core/services';
import { anyApi } from 'convex/server';
import { Chat } from './types/chat.type';
import {
    UiMessage,
    UiTextMessage,
    UiToolMessage,
} from './types/ui-message.type';
import { Subject, Subscription } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Message } from './types/message.type';
import { StreamData } from './types/stream-data.type';
import {
    MessageContent,
    MessageToolResultContent,
} from './types/message-content.type';

@Injectable()
export class ChatService {
    public chats: Chat[] = [];

    public selection:
        | {
              chat: Chat;
              messages: UiMessage[];
          }
        | undefined;
    public isSending = false;

    public streams: {
        streamId: string;
        subscription: Subscription;
        messages: UiMessage[];
    }[] = [];

    public get canSend(): boolean {
        return !this.isProcessing;
    }

    public get isProcessing(): boolean {
        return this.isSending || this.isStreaming;
    }

    public get isStreaming(): boolean {
        return this.selection?.chat.isStreaming;
    }

    private scheme: 'guide' | 'tournament';

    private messagesSubscription: Subscription | undefined = undefined;

    private onNewMessage?: () => void;

    private unsubscribeAll = new Subject<void>();

    public constructor(private readonly convexService: ConvexService) {}

    public start(
        scheme: 'guide' | 'tournament',
        onNewMessage?: () => void
    ): void {
        this.scheme = scheme;
        this.onNewMessage = onNewMessage;

        this.convexService
            .query(anyApi.public.chats.list, { scheme: this.scheme })
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(chats => {
                this.chats = chats;
                if (!this.selection) return;

                // We need to update the chat object to the latest
                // copy from convex
                this.selection.chat = this.chats.find(
                    x => x._id === this.selection.chat._id
                );
            });
    }

    public stop(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    public selectChat(chat: Chat): void {
        if (this.selection?.chat._id === chat._id) return;

        this.deselectChat();

        this.selection = { chat, messages: [] };

        this.messagesSubscription = this.convexService
            .query(anyApi.public.messages.list, {
                threadId: chat.threadId,
                paginationOpts: {
                    numItems: 50,
                    cursor: null,
                },
                streamArgs: {
                    kind: 'list',
                },
            })
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(data => {
                const page = data.page as Message[];

                const streams = data.streams as {
                    messages: { streamId: string }[];
                };

                this.manageStreams(streams.messages.map(x => x.streamId));

                if (this.isStreaming) return;

                // Turn Convex messages into UiMessages
                const newMessages: UiMessage[] = page
                    .slice()
                    .reverse()
                    .flatMap(msg => {
                        const result: UiMessage[] = [];

                        if (msg.message.role === 'user') {
                            result.push({
                                id: msg._id,
                                role: 'user',
                                text: msg.text,
                            });
                            return result;
                        }

                        if (msg.message.role === 'assistant') {
                            if (typeof msg.message.content === 'string') {
                                result.push({
                                    id: msg._id,
                                    role: 'assistant',
                                    text: msg.message.content,
                                });
                                return result;
                            }

                            (msg.message.content as MessageContent[]).forEach(
                                content => {
                                    if (content.type === 'text') {
                                        result.push({
                                            id: msg._id,
                                            role: 'assistant',
                                            text: msg.text,
                                        });
                                    } else if (content.type === 'tool-call') {
                                        // pending tool call
                                        result.push({
                                            id: `${msg._id}:${content.toolCallId}`,
                                            role: 'tool',
                                            toolCallId: content.toolCallId,
                                            toolName: content.toolName,
                                            status: 'pending',
                                        });
                                    }
                                }
                            );

                            return result;
                        }

                        if (msg.message.role === 'tool') {
                            const content = (
                                msg.message.content as MessageContent[]
                            ).find(c => c.type === 'tool-result') as
                                MessageToolResultContent | undefined;

                            if (!content) return result;

                            // completed tool result
                            result.push({
                                id: `${msg._id}:${content.toolCallId}`,
                                role: 'tool',
                                toolCallId: content.toolCallId,
                                toolName: content.toolName,
                                status: 'success',
                            });

                            return result;
                        }

                        return result;
                    });

                // 1) Append only non-duplicate messages by id
                const existingIds = new Set(
                    this.selection.messages.map(m => m.id)
                );
                const uniqueNew = newMessages.filter(
                    m => !existingIds.has(m.id)
                );

                this.selection.messages.push(...uniqueNew);

                // 2) Consolidate tool-call + tool-result pairs
                const toolsByCallId = new Map<string, UiToolMessage>();

                for (const m of this.selection.messages) {
                    if (m.role !== 'tool') continue;

                    const existing = toolsByCallId.get(m.toolCallId);
                    if (!existing) {
                        toolsByCallId.set(m.toolCallId, m);
                    } else {
                        // Prefer success over pending
                        if (
                            existing.status === 'pending' &&
                            (m.status === 'success' || m.status === 'failure')
                        ) {
                            toolsByCallId.set(m.toolCallId, m);
                        }
                    }
                }

                const consolidated: UiMessage[] = [];

                for (const m of this.selection.messages) {
                    if (m.role !== 'tool') {
                        consolidated.push(m);
                        continue;
                    }

                    const kept = toolsByCallId.get(m.toolCallId);
                    if (kept === m) {
                        consolidated.push(m);
                    }
                    // if not the kept one, drop it
                }

                this.selection.messages = consolidated.filter(
                    x => x.role === 'tool' || x.text.trim()
                );

                this.onNewMessage?.();
            });
    }

    public deselectChat(): void {
        if (!this.selection) return;

        // Stop loading new messages for the older chat
        this.messagesSubscription?.unsubscribe();

        // Stop all streams
        this.stopAllStreams();

        this.selection = undefined;
    }

    public deleteChat(chat: Chat): void {
        if (this.selection?.chat._id === chat._id) {
            this.deselectChat();
        }

        this.convexService
            .mutation(anyApi.public.chats.deleteOne, {
                chatId: chat._id,
            })
            .subscribe();
    }

    public stopChat(): void {
        if (!this.selection || !this.isStreaming) return;
        this.convexService
            .mutation(anyApi.public.chats.stopByThreadId, {
                threadId: this.selection.chat.threadId,
            })
            .subscribe(() => {
                this.stopAllStreams();
            });
    }

    public sendMessage(prompt: string): void {
        if (this.isProcessing || !prompt) return;

        this.isSending = true;

        const send = (): void => {
            this.convexService
                .mutation(anyApi.public.messages.send, {
                    threadId: this.selection.chat.threadId,
                    prompt,
                })
                .pipe(finalize(() => (this.isSending = false)))
                .subscribe();
        };

        // Create chat first if no selection
        if (!this.selection) {
            this.convexService
                .action(anyApi.public.chats.create, {
                    scheme: this.scheme,
                    firstPrompt: prompt,
                })
                .subscribe(chat => {
                    this.selectChat(chat);
                    send();
                });
        } else {
            send();
        }
    }

    private manageStreams(streamIds: string[]): void {
        if (!this.selection) return;

        streamIds.forEach(streamId => {
            if (this.streams.find(x => x.streamId === streamId)) return;

            this.streams.push({
                streamId,
                subscription: this.startStream(streamId),
                messages: [],
            });
        });
    }

    private startStream(streamId: string): Subscription {
        if (!this.selection) return;

        // local buffer + timer for THIS stream
        let pending = '';
        let flushTimer: number | null = null;
        const chunkSize = 10; // chars per tick
        const intervalMs = 50; // fixed period

        const startFlushTimer = (): void => {
            if (flushTimer !== null) return;

            flushTimer = window.setInterval(() => {
                if (!pending.length) {
                    // nothing left → stop timer
                    if (flushTimer !== null) {
                        clearInterval(flushTimer);
                        flushTimer = null;
                    }
                    return;
                }

                const chunk = pending.slice(0, chunkSize);
                pending = pending.slice(chunkSize);

                const stream = this.streams.find(x => x.streamId === streamId);
                if (!stream) return;

                const textMessages = stream.messages.filter(
                    x => x.role === 'assistant'
                );
                const lastTextMessage = textMessages[
                    textMessages.length - 1
                ] as UiTextMessage;

                if (!lastTextMessage) return;

                lastTextMessage.text += chunk;

                this.onNewMessage?.();
            }, intervalMs);
        };

        const subject = new Subject<number>();

        const subscription = subject
            .pipe(
                switchMap(cursor => {
                    return this.convexService.query(
                        anyApi.public.messages.list,
                        {
                            threadId: this.selection.chat.threadId,
                            paginationOpts: { numItems: 0, cursor: null },
                            streamArgs: {
                                kind: 'deltas',
                                cursors: [{ streamId, cursor }],
                            },
                        }
                    );
                }),
                takeUntil(this.unsubscribeAll)
            )
            .subscribe((data: StreamData) => {
                const deltas = data.streams.deltas;

                if (!deltas.length) return;

                const lastDelta = deltas[deltas.length - 1];

                const stream = this.streams.find(x => x.streamId === streamId);

                for (const part of lastDelta.parts) {
                    switch (part.type) {
                        case 'text-start':
                            stream.messages.push({
                                id: new Date().getTime().toString(),
                                role: 'assistant',
                                text: '',
                            });
                            break;

                        case 'text-delta':
                            pending += part.delta;
                            startFlushTimer();

                            break;

                        case 'tool-input-start':
                            stream.messages.push({
                                role: 'tool',
                                id: part.toolCallId,
                                status: 'pending',
                                toolName: part.toolName,
                                toolCallId: part.toolCallId,
                            });
                            this.onNewMessage?.();
                            break;

                        case 'tool-output-available':
                            const toolMessage = stream.messages.find(
                                x =>
                                    x.role === 'tool' &&
                                    x.toolCallId === part.toolCallId
                            ) as UiToolMessage;
                            if (!toolMessage) break;

                            toolMessage.status = 'success';
                            toolMessage.output = part.output;
                            break;

                        case 'finish':
                            stream.subscription.unsubscribe();
                            this.streams = this.streams.filter(
                                x => x !== stream
                            );
                            break;
                    }
                }

                subject.next(lastDelta.end);
            });

        subject.next(0);

        return subscription;
    }

    private stopAllStreams(): void {
        this.streams.forEach(x => x.subscription.unsubscribe());
        this.streams = [];
    }
}
